import { FC, PointerEvent as PointerEventReact, RefObject } from "react";
import { ICalendarLesson, ICalendarSlot, TLessonStatus } from "../../../../entities/calendar";
import { CALENDAR_DAY_SLOT_HEIGHT_PX } from "../../../../shared/lib/const/calendarDay";
import { formatLessonsCount } from "../../../../shared/lib/helpers/formatRuCount";
import { useCalendarDayEventsLayer } from "../../../../shared/lib/hooks/useCalendarDayEventsLayer";
import { IActiveSelection, IDisplayedPositionedLesson, IGroupedLessons } from "../../lib/types";
import { CalendarDayLessonBlock } from "../lessonBlock/CalendarDayLessonBlock";
import { CalendarLessonsPopover } from "../lessonsPopover/CalendarLessonsPopover";
import { getMoscowDateTimeParts } from "../../../../shared/lib/helpers/moscowDateTime";
import { canCreateLessonInSlot, canSelectLessonCreateSlot } from "../../lib/canCreateLessonInSlot";
import classes from '../calendarDay.module.scss'

interface IProps {
    slotsRef: RefObject<HTMLUListElement | null>;
    slots: Date[];
    calendarSlotsByIndex: Map<number, ICalendarSlot>;
    activeSelection: IActiveSelection | null;
    visibleLessons: IDisplayedPositionedLesson[];
    groupedLessons: IGroupedLessons[];
    compactTime: boolean;
    formatTime: (date: Date) => string;
    getLessonTimeRange: (lesson: ICalendarLesson) => string;
    getLessonStatusClass: (status: TLessonStatus) => string;
    onPointerDown: (event: PointerEventReact<HTMLLIElement>, slotIndex: number) => void;
    onPointerMove: (event: PointerEventReact<HTMLUListElement>) => void;
    onPointerUp: (event: PointerEventReact<HTMLUListElement>) => void;
    onPointerCancel: () => void;
    onSelectLesson: (lesson: ICalendarLesson) => void;
}

export const CalendarDaySlots: FC<IProps> = ({
    slotsRef,
    slots,
    calendarSlotsByIndex,
    activeSelection,
    visibleLessons,
    groupedLessons,
    compactTime,
    formatTime,
    getLessonTimeRange,
    getLessonStatusClass,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onSelectLesson,
}) => {
    const {eventsLayerRef, eventsLayerWidth, getEventPosition} =
        useCalendarDayEventsLayer(formatLessonsCount)

    return (
        <ul
            ref={slotsRef}
            className={classes.slots}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
        >
            {slots.map((slotDate, slotIndex) => {
                const calendarSlot = calendarSlotsByIndex.get(slotIndex)
                const isAvailable = canCreateLessonInSlot(calendarSlot)
                const isSelectable = canSelectLessonCreateSlot(calendarSlot)
                const isUnavailable = !isAvailable
                const isSelected = activeSelection &&
                    slotIndex >= activeSelection.startIndex &&
                    slotIndex <= activeSelection.endIndex

                return (
                    <li
                        data-slot-index={slotIndex}
                        key={slotDate.getTime()}
                        className={
                            classes.slot +
                            (isSelected ? ` ${classes.selected}` : '') +
                            (isAvailable ? ` ${classes.available}` : '') +
                            (isSelectable ? ` ${classes.selectable}` : '') +
                            (isUnavailable ? ` ${classes.unavailable}` : '')
                        }
                        onPointerDown={event => onPointerDown(event, slotIndex)}
                        onDrag={event => event.preventDefault()}
                        onDragStart={event => event.preventDefault()}
                    >
                        {getMoscowDateTimeParts(slotDate).minutes === 0 && (
                            <span className={classes.hour}>{formatTime(slotDate)}</span>
                        )}
                    </li>
                )
            })}
            <li className={classes.slotHour}>
                <span className={classes.hour}>{compactTime ? '24' : '24:00'}</span>
            </li>
            <li ref={eventsLayerRef} className={classes.eventsLayer}>
                {visibleLessons.map(positionedLesson => {
                    const position = getEventPosition(positionedLesson.columnsLayout, false)

                    return (
                        <CalendarDayLessonBlock
                            key={positionedLesson.lesson.id}
                            lesson={positionedLesson.lesson}
                            startIndex={positionedLesson.startIndex}
                            endIndex={positionedLesson.endIndex}
                            left={position.left}
                            width={position.width}
                            zIndex={position.zIndex}
                            visibleStartsAt={positionedLesson.visibleStartsAt}
                            visibleEndsAt={positionedLesson.visibleEndsAt}
                            formatTime={formatTime}
                            statusClassName={getLessonStatusClass(positionedLesson.lesson.status)}
                            onSelect={onSelectLesson}
                        />
                    )
                })}
                {groupedLessons.map(group => {
                    const position = getEventPosition(group.columnsLayout, true)

                    return (
                        <CalendarLessonsPopover
                            key={`${group.status}-${group.startIndex}-${group.lane}`}
                            lessons={group.events.map(event => event.lesson)}
                            top={group.startIndex * CALENDAR_DAY_SLOT_HEIGHT_PX}
                            left={position.left}
                            width={position.width}
                            height={Math.max(
                                group.durationSlots * CALENDAR_DAY_SLOT_HEIGHT_PX - 2,
                                CALENDAR_DAY_SLOT_HEIGHT_PX - 2
                            )}
                            zIndex={position.zIndex}
                            openToLeft={position.left + position.width + 320 > eventsLayerWidth}
                            getLessonTimeRange={getLessonTimeRange}
                            getLessonStatusClass={getLessonStatusClass}
                            statusClassName={getLessonStatusClass(group.status)}
                            onSelectLesson={onSelectLesson}
                        />
                    )
                })}
            </li>
        </ul>
    )
}
